import { useEffect } from "react";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useQueryClient } from "@tanstack/react-query";
import { gql } from "@/services/httpAdapter";
import { ISummaryDetail } from "@/DataPages/types/inventory/models/reconciliation";

type TUseReconciliationSummary = {
	locationId?: string;
	commissionCycleId?: string;
	payPeriodId?: string;
	vehicleType?: string;
};

const useReconciliationSummarySubscription = (params: TUseReconciliationSummary) => {
	const { locationId = "", commissionCycleId = "", payPeriodId = "" } = params;
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();

	const setReconciliationSummarySubscriptions = getSubscription([
		"onSetReconciliationSummary",
		locationId,
		commissionCycleId,
		payPeriodId,
	]);

	setReconciliationSummarySubscriptions?.subscriptions?.onSetReconciliationSummary?.setCallback(
		(result: { data: { onSetReconciliationSummary: ISummaryDetail } }) => {
			const vehicleType = result.data.onSetReconciliationSummary.vehicleType;
			queryClient.setQueryData<ISummaryDetail>(
				["inventory", "summaryData", vehicleType, locationId, commissionCycleId, payPeriodId],
				result.data.onSetReconciliationSummary
			);
		}
	);

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["onSetReconciliationSummary", locationId, commissionCycleId, payPeriodId],
				[
					{
						context: "onSetReconciliationSummary",
						query: gql`
							subscription onSetReconciliationSummary($locationId: String, $commissionCycleId: String, $payPeriodId: String) {
								onSetReconciliationSummary(
									locationId: $locationId
									commissionCycleId: $commissionCycleId
									payPeriodId: $payPeriodId
								) {
									storeNumber
									inventoryDate
									reconciliationCompletionDate
									dealership
									# Below properties are common for all types of vehicles (New and Used)
									physicalInventoryCountOnLot
									physicalInventoryCountOffLot
									unitsOnScheduleNotOnLot
									unitsOnLotNotOnSchedule
									unitsOffLotNotOnSchedule
									perGeneralLedgerSchedule
									totalSchedule
									differenceSchedule
									# Below properties are only for new vehicles
									physicalInventoryOnLot
									physicalInventoryOffLot
									unitsInPhysicalInventoryNotOnMfg
									unitsOffPhysicalInventoryNotOnMfg
									perManufacturerReport
									totalManufacture
									differenceManufacture

									locationId
									commissionCycleId
									payPeriodId
									vehicleType
								}
							}
						`,
						variables: { locationId, commissionCycleId, payPeriodId },
					},
				]
			);
		}
		return () => {
			deleteSubscription(["onSetReconciliationSummary", locationId, commissionCycleId, payPeriodId]);
		};
	}, [locationId, commissionCycleId, payPeriodId]);
};

export default useReconciliationSummarySubscription;
